import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

import { AgentMilestones } from '../../../../store/modules/merchant/types';
import { formatCount, formatRate } from './formatMetrics';

type AgentMilestonesPanelProps = {
  milestones?: AgentMilestones;
  /** All sessions in range; each bar's percent is this share. */
  sessionCount?: number;
  isLoading?: boolean;
};

type MilestoneBarItem = {
  barKey: string;
  label: string;
  count: number | undefined;
  shareTotal: number;
  rank: number;
  isLast: boolean;
};

/** Agent milestones widget: neutral gray insight bars. */
const INSIGHT_BAR = '#6B7280';
const INSIGHT_TEXT = '#374151';
const INSIGHT_TRACK = '#E8EAED';

const MilestoneBar = ({ barKey, label, count, shareTotal, rank, isLast }: MilestoneBarItem) => {
  const share = shareTotal > 0 && count != null ? count / shareTotal : null;
  const barPct = ((share ?? 0) * 100).toFixed(1);
  const metaParts = [
    count != null ? `${formatCount(count)} sessions` : null,
    `stage ${rank}`,
  ].filter(Boolean);

  return (
    <Box
      component="li"
      data-testid={`ella-milestone-bar-${barKey}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: '32px 1fr 76px',
        gap: 1.5,
        alignItems: 'center',
        py: 1.5,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(107, 114, 128, 0.14)',
          color: INSIGHT_TEXT,
          typography: 'small',
          fontWeight: 700,
        }}
      >
        {rank}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="small" sx={{ lineHeight: 1.35 }}>
          {label}
        </Typography>
        <Typography
          variant="small"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
        >
          {metaParts.join(' · ')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
          width: 76,
        }}
        aria-hidden
      >
        <Box
          sx={{
            width: '100%',
            height: 6,
            borderRadius: 999,
            backgroundColor: INSIGHT_TRACK,
            overflow: 'hidden',
          }}
        >
          <Box
            data-testid={`ella-milestone-fill-${barKey}`}
            sx={{
              height: '100%',
              width: `${barPct}%`,
              minWidth: count ? 3 : 0,
              borderRadius: 999,
              backgroundColor: INSIGHT_BAR,
            }}
          />
        </Box>
        <Typography
          variant="small"
          fontWeight="semibold"
          sx={{ color: INSIGHT_TEXT, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}
        >
          {share != null ? formatRate(share) : formatCount(count)}
        </Typography>
      </Box>
    </Box>
  );
};

export const AgentMilestonesPanel = ({
  milestones,
  sessionCount,
  isLoading,
}: AgentMilestonesPanelProps) => {
  const shareTotal = sessionCount || 0;
  const bars: MilestoneBarItem[] = [
    {
      barKey: 'session',
      label: 'Initial conversation',
      count: milestones?.sessionReached,
      shareTotal,
      rank: 1,
      isLast: false,
    },
    {
      barKey: 'product',
      label: 'Product Details (PDP)',
      count: milestones?.productReached,
      shareTotal,
      rank: 2,
      isLast: false,
    },
    {
      barKey: 'cart',
      label: 'Cart Review',
      count: milestones?.cartReached,
      shareTotal,
      rank: 3,
      isLast: false,
    },
    {
      barKey: 'checkout',
      label: 'Checkout',
      count: milestones?.checkoutReached,
      shareTotal,
      rank: 4,
      isLast: true,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'secondary.main',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
      data-testid="ella-milestone-panel"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="medium"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', lineHeight: '18px' }}
        >
          Agent milestones
        </Typography>
        <Typography variant="small" color="text.secondary" sx={{ lineHeight: '16px' }}>
          Sessions that hit each milestone.
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Skeleton variant="text" width="55%" height={18} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        </Box>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label="Agent milestones">
          {bars.map((bar) => (
            <MilestoneBar key={bar.barKey} {...bar} />
          ))}
        </Box>
      )}
    </Box>
  );
};
